import React, { useEffect, useState } from "react";
import { Progress } from "reactstrap";
import type { AnalysisResponse, AnalysisResult, Analysis, Skills } from "../../constants/analysisResultsFormat";
import colors from "../../constants/colors";
import { CONVERSATION_STAGE, EmbedButton, Column, headerColor } from "../../constants/conv_common";
import { AnalysisError, classifyAnalysisError, classifyEmptyAnalysisResponse } from "../../helpers/analysisErrors";
import { ifxApiClient } from "../../helpers/api";
import { detectBrowserAndOS } from "../../helpers/detectBrowser";
import MicTestModal from "./MicTestModal";
import { extractAbilities, getAbilityStatusText, getScoreText } from "./ScoringAndFeedback";  

const SUPPORT_EMAIL = "support@interflexion.com"; // ideally share this constant with RecordScreen
// ***********************************************************
// The following helper functions support the analysis results

// *****    UI Components    *****
const AnalysisCard = ({ children }: { children: React.ReactNode }) => (
  <div
    style={{
      padding: "10px",
      boxShadow: "0px 0px 5px 4px rgba(0,0,0,0.1)",
      borderRadius: "10px",
      margin: "20px 0",
      minWidth: "350px",  
      maxWidth: "100%",
    }}
  >
    {children}
  </div>
);

interface StoryAnalysisScreenProps {
  storyId: string;
  sceneId: string;
  mustPass: boolean;
  lastScene?: boolean;
  userId: string;
  interactUserId: string;  
  audioUrl?: string;
  analysisResponse: any;  // Consider adding a more specific type here
  onStageChange: (newStage: CONVERSATION_STAGE) => Promise<void>;
  onContinueConversation: () => Promise<void>;
  onAnalysisResponse: (newAnalysisResponse: any) => void;  // Match the type to your analysis response structure
}

interface UtterancePayload {
  uid: string;
  storyid: string;
  sceneid: string;
  uri: string | undefined;
  localfilename: string | null;
  localaudiofilename: string | null;
  method: string;
  webBrowser: string;
  os: string;
}

// ************************************************************************************************
// The Main Function:   AnalysisScreen

const StoryAnalysisScreen = ({
  storyId,
  sceneId,
  mustPass,
  lastScene,
  userId,
  interactUserId,
  audioUrl,
  onStageChange,
  onContinueConversation,
  onAnalysisResponse,
  analysisResponse: upstreamAnalysisResponse,
}: StoryAnalysisScreenProps) => {
    // clearly separate local and upstream state:
    const [analysisState, setAnalysisState] = useState<{
      isLoading: boolean;
      error?: AnalysisError;
      responseData?: AnalysisResponse | null;
    }>({
      isLoading: false,
      responseData: upstreamAnalysisResponse || null,
    });

  const [micTestOpen, setMicTestOpen] = useState(false);

  // console.info("IFX: Entered AnalysisScreen:"); // DEBUG

  const performAnalysis = async (payload: UtterancePayload) => {
    try {
      const httpResponse = await ifxApiClient.post<AnalysisResponse>("/analysis/analyzeembed", payload);
      const analysisData = httpResponse.data;
      if (!analysisData?.analyses) {
        // a 2xx without analyses means no speech was detected (server sends code EMPTY_STT)
        setAnalysisState(prevState => ({
          ...prevState,
          error: classifyEmptyAnalysisResponse(analysisData),
          isLoading: false,
          responseData: null
        }));
        return null;
      }
    // Update our state with just the analysis data
      setAnalysisState(prevState => ({
        ...prevState,
        responseData: analysisData,  // This is now just the AnalysisResponse
        isLoading: false
      }));
    // Pass just the analysis data to our callback
    onAnalysisResponse?.(analysisData);
    return analysisData;  // Return just the analysis data

    } catch (error) {
      const analysisError: AnalysisError = classifyAnalysisError(error);
      console.error(`Analysis failed (${analysisError.type}):`, analysisError.details);
      setAnalysisState(prevState => ({
        ...prevState,
        error: analysisError,
        isLoading: false,
        responseData: null
      }));
      return null;
    }
  };

  const submitAnalysis = () => {
    setAnalysisState(prev => ({ ...prev, error: undefined, isLoading: true }));
    const browserAndOS = detectBrowserAndOS();
    // console.log("********************************************");
    // console.log("Interaction AnalysisScreen about to post content to analysis/analyzeembed ");
    // console.log("St, Sc, uid iuid: ", storyId, sceneId, userId, interactUserId);
    // console.log("vid, br, os", audioUrl, browserAndOS.browser, browserAndOS.os);
    const analysisUID = interactUserId || userId;
    // console.info("IFX: AnalysisScreen analysisUID: ", analysisUID);
    const utterancePayload = {
      uid: analysisUID,
      storyid: storyId,
      sceneid: sceneId,
      uri: audioUrl,
      localfilename: null,
      localaudiofilename: null,
      method: "web",
      webBrowser: browserAndOS.browser,
      os: browserAndOS.os,
      };

    performAnalysis(utterancePayload);
  };

  useEffect(() => {
    // Only start analysis if we don't have a response and aren't already loading
    if (analysisState.responseData || analysisState.isLoading) {
      return;
    }
    submitAnalysis();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  let content;

  if (analysisState.error) {
    const { type: errorType, canResubmit } = analysisState.error;
    const showMicTest = errorType === "NO_SPEECH" || errorType === "STT_FAILURE";
    content = (
      <div
        style={{
          marginTop: "100px",
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "center",
          flexWrap: "wrap",
        }}
      >
        <Column>
          <h5
            style={{
              textAlign: "center",
            }}
          >
            { analysisState.error.message }
          </h5>
          <div>
            {canResubmit && (
              <EmbedButton onClick={submitAnalysis}>
                Try again
              </EmbedButton>
            )}
            {showMicTest && (
              <EmbedButton onClick={() => setMicTestOpen(true)}>
                Test my microphone
              </EmbedButton>
            )}
            {errorType === "NOT_AUTHORIZED" ? (
              <EmbedButton onClick={() => onStageChange(CONVERSATION_STAGE.STORY_QUIT)}>
                Quit
              </EmbedButton>
            ) : (
              <EmbedButton
                onClick={() => {
                  setAnalysisState(prev => ({ ...prev, error: undefined }));
                  onAnalysisResponse?.(null);
                  onStageChange(CONVERSATION_STAGE.RESPONSE_VIDEO_RECORDING);
                }}
              >
                Record again
              </EmbedButton>
            )}
            {(errorType === "NOT_AUTHORIZED" || errorType === "SERVER_ERROR") && (
              <p style={{ textAlign: "center", marginTop: "10px" }}>
                Need help? Contact <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
              </p>
            )}
          </div>
          <MicTestModal
            isOpen={micTestOpen}
            onClose={() => setMicTestOpen(false)}
            supportEmail={SUPPORT_EMAIL}
            onRetry={() => {
              setMicTestOpen(false);
              onStageChange(CONVERSATION_STAGE.RESPONSE_VIDEO_RECORDING);
            }}
          />
        </Column>
      </div>
    );
  } else if (!analysisState.responseData) {
    content = (
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          alignItems: "flex-start",
          justifyContent: "center",
          flexWrap: "wrap",
        }}
      >
        <h4>Analyzing…</h4>
      </div>
    );
  } else {
    // console.info("response data", analysisState.responseData ); // DEBUG
    // console.info("Transcript", analysisState.responseData.transcript ); // DEBUG
    const parsedAbilities = extractAbilities(analysisState.responseData);
    const score = analysisState?.responseData?.skills?.current.score || 0;
    content = (
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          alignItems: "flex-start",
          justifyContent: "center",
          flexWrap: "wrap",
        }}
      >
        <Column>
          <h3 style={{ color: headerColor }}>
            Overall Effectiveness
            </h3>
          <AnalysisCard>
            <h4 style={{ color: headerColor, textAlign: "center" }}>{analysisState?.responseData?.skills?.name}</h4>

            <h2 style={{ color: headerColor, textAlign: "center" }}>
              {Math.trunc(analysisState?.responseData?.skills?.current.score * 100)}
            </h2>
            <Progress value={score * 100} />

            <div style={{ marginTop: "20px" }}>{getScoreText(analysisState?.responseData?.passed, score)}</div>
          </AnalysisCard>

          <div>
            {(analysisState?.responseData?.passed) && ( <div>
              <EmbedButton backgroundColor={colors.IFX_BRAND_LIME}
                  onClick={() => {
                    if (lastScene) onStageChange(CONVERSATION_STAGE.STORY_PASSED)
                    else {
                      onAnalysisResponse?.(null);
                      onContinueConversation();
                    }
                  }}
                >
                {lastScene ? "Congratulations!" : "Continue the Conversation"}     
                </EmbedButton>
                </div> ) }

                {((!analysisState?.responseData?.passed) && !lastScene && !mustPass) && ( <div>
              <EmbedButton 
                  onClick={() => {
                    if (lastScene) onStageChange(CONVERSATION_STAGE.STORY_PASSED)
                    else {
                      onAnalysisResponse?.(null);
                      onContinueConversation();
                    }
                  }}
                >
                 Continue the Conversation    
                </EmbedButton>
                </div> ) }

            <EmbedButton
              onClick={() => {
                onAnalysisResponse?.(null);
                onStageChange(CONVERSATION_STAGE.INTERACTION_VIDEO);
              }}
            >
              Try again
            </EmbedButton>
            <EmbedButton
              onClick={() => {
                onStageChange(CONVERSATION_STAGE.EXPERT_VIDEO);
              }}
            >
              View a Good Response
            </EmbedButton>
            <EmbedButton
              onClick={() => {
                onStageChange(CONVERSATION_STAGE.STORY_QUIT);
              }}
            >
              {lastScene ? "Done" : "Quit"}
            </EmbedButton>
          </div>
        </Column>

        <Column>
          <h3 style={{ color: headerColor }}>Feedback</h3>
          {parsedAbilities.map(({ name, description, points, feedback, numRequired, priority, possiblePoints, abilityPassed }) => (
            <AnalysisCard key={name}>
              <h4 style={{ color: headerColor }}>{name}</h4>
              <p style={{ color: "gray", marginLeft: "20px" }}>
              </p>
              {getAbilityStatusText(name, priority, feedback.length, numRequired, abilityPassed)}
              {/* <p>{description}</p> */}

              <div style={{ margin: "10px 0" }}>
                {feedback.map(({ feedback, isPositive }) => {
                  const icon = isPositive ? "icon-plus" : "icon-minus";
                  const color = isPositive ? "text-success" : "text-danger";
                  return (
                    <span className={color} key={feedback}>
                      {" "}
                      <i className={icon} /> {feedback}
                      <br />
                    </span>
                  );
                })}
              </div>

              <p style={{ textAlign: "right" }} className={points ? "text-success" : "text-danger"}>
                {Math.round(points)} points (out of {possiblePoints})
              </p>
            </AnalysisCard>
          ))}
        </Column>

        <Column>
          <h3 style={{ color: headerColor }}>We Heard You Say</h3>
          <AnalysisCard>
            <p>{analysisState?.responseData?.transcript}</p>
          </AnalysisCard>
        </Column>
      </div>
    );
  }

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        width: "100%",
        height: "100%",
        minHeight: "100%",
        maxWidth: "100%",
      }}
    >
      {content}
    </div>
  );
};

export default StoryAnalysisScreen;
